import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Hammer, Warning, Flask } from '@phosphor-icons/react';
import ChangeLotDialog from './ChangeLotDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GR10_MATIERES = ['resine_3d', 'resine', 'résine_3d'];
export const deviceUsesGr10 = (d) => d.material_ref === 'A1000800' || GR10_MATIERES.includes(d.specs?.matiere);

// Démarrage effectif de production : bac/machine + lot actif exigé (GR-10), profil, opérateur, horodatage.
export default function StartProductionDialog({ orderId, order, user, open, onOpenChange, onStarted }) {
  const [tanks, setTanks] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tankId, setTankId] = useState('');
  const [profile, setProfile] = useState('');
  const [loading, setLoading] = useState(false);
  const [changeLotOpen, setChangeLotOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([axios.get(`${API}/resin-tanks`), axios.get(`${API}/devices`, { params: { order_id: orderId } })]);
      setTanks(t.data.items || []);
      setDevices(d.data.items || []);
      setTankId((prev) => prev || (t.data.items?.length === 1 ? t.data.items[0].id : ''));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
  }, [orderId]);

  useEffect(() => { if (open && orderId) { setTankId(''); setProfile(''); load(); } }, [open, orderId, load]);

  const tank = tanks.find(t => t.id === tankId);
  useEffect(() => { if (tank) setProfile(tank.default_print_profile || ''); }, [tank?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = devices.filter(d => !d.production_start);
  const needsLot = pending.some(deviceUsesGr10);
  const lotMissing = needsLot && !(tank && tank.active_lot_id);
  const canStart = !loading && (!needsLot || (tank && tank.active_lot_id));

  const start = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/orders/${orderId}/start-fabrication`, { tank_id: tankId || null, print_profile: profile || null });
      const p = r.data.production;
      toast.success(`Fabrication démarrée${p?.lot_number ? ` — lot ${p.lot_number}` : ''}`);
      onStarted?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white max-w-[92vw] sm:max-w-lg" data-testid="start-production-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Hammer size={18} />Démarrer la production</DialogTitle>
            <DialogDescription>{order?.patient_reference ? `Réf. patient ${order.patient_reference} · ` : ''}Opérateur : <strong>{user?.name}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Dispositifs concernés</p>
              {devices.length === 0 ? <p className="text-sm italic text-zinc-400">Aucune fiche dispositif</p> : (
                <ul className="text-sm space-y-0.5" data-testid="start-prod-devices">
                  {devices.map(d => (
                    <li key={d.id} className="flex items-center gap-2" data-testid={`start-prod-device-${d.side}`}>
                      <span className="font-mono font-semibold">{d.hamac_id}</span>
                      <span className="text-zinc-500">{d.side === 'right' ? 'DROIT' : 'GAUCHE'} · {d.serial_number}</span>
                      {deviceUsesGr10(d) ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">GR-10</span> : <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">Sans lot résine</span>}
                      {d.production_start && <span className="text-[10px] text-emerald-700">déjà démarré — lot {d.production_start.lot_number || '—'} conservé</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Label className="text-xs">Bac / machine {needsLot && '*'}</Label>
              <Select value={tankId} onValueChange={setTankId}>
                <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="start-prod-tank-select"><SelectValue placeholder="Choisir le bac utilisé" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {tanks.map(t => (
                    <SelectItem key={t.id} value={t.id} data-testid={`tank-option-${t.id}`}>
                      {t.name}{t.machine ? ` — ${t.machine}` : ''} · {t.active_lot_number ? `lot ${t.active_lot_number}` : 'aucun lot actif'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={`p-3 border rounded-sm ${lotMissing ? 'border-red-300 bg-red-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="start-prod-lot-box">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400">Lot de résine actif {needsLot ? '(requis)' : '(non requis pour cette commande)'}</p>
                  <p className="font-mono font-semibold" data-testid="start-prod-active-lot">{tank?.active_lot_number || <span className="italic font-sans font-normal text-red-700">{tank ? 'Aucun lot actif sur ce bac' : 'Sélectionnez un bac'}</span>}</p>
                  <p className="text-[11px] text-zinc-500">pro3dure audioprint GR-10 clear-transparent · REF A1000800</p>
                </div>
                {tank && <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setChangeLotOpen(true)} data-testid="start-prod-change-lot-btn"><Flask size={14} className="mr-1" />{tank.active_lot_number ? 'Changer le lot' : 'Renseigner le lot'}</Button>}
              </div>
              {lotMissing && <p className="flex items-center gap-1 text-xs text-red-700 mt-2" data-testid="start-prod-lot-missing"><Warning size={14} />Un lot actif est obligatoire pour démarrer une production GR-10.</p>}
            </div>
            <div>
              <Label className="text-xs">Profil d'impression</Label>
              <Input value={profile} onChange={(e) => setProfile(e.target.value)} placeholder="ex. GR-10 50 µm standard" className="rounded-sm mt-1" data-testid="start-prod-profile-input" />
            </div>
            <p className="text-[11px] text-zinc-500">Le lot, la machine, le profil, l'opérateur et l'horodatage sont figés sur chaque fiche dispositif au démarrage et ne changent plus, même si le lot actif du bac évolue ensuite.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={start} disabled={!canStart} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-white" data-testid="start-prod-confirm-btn">{loading ? 'Démarrage…' : 'Démarrer la fabrication'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ChangeLotDialog tank={tank} open={changeLotOpen} onOpenChange={setChangeLotOpen} onChanged={() => load()} />
    </>
  );
}
